import React from 'react'
import striptags from 'striptags'
import { useClientOptions } from '@paintscout/ui-business'
import { getDetails, getItems, getQuoteOptions, getTotals } from '@paintscout/util/builder'
import WorkAreaSummaryGrid from './WorkAreaSummaryGrid'
import WorkItemRow from './WorkItemRow'
import type { PainterWorkAreaSummary, PainterWorkItem, PainterWorkSubarea } from './types'
import type { QuoteDocument, OptionsDocument } from 'paintscout'
import type { usePainterWorkOrderStyles } from './usePainterWorkOrderStyles'

interface WorkDetailsProps {
  sourceQuote?: QuoteDocument
  showHours: boolean
  classes: ReturnType<typeof usePainterWorkOrderStyles>
}

function hasContent(html: string | undefined): boolean {
  return !!html && !!striptags(html).trim()
}

export default function WorkDetails({ sourceQuote, showHours, classes }: WorkDetailsProps) {
  const { options } = useClientOptions()

  let workAreaSummary: PainterWorkAreaSummary | undefined
  let workItems: PainterWorkItem[] = []

  const allowShowHours =
    !sourceQuote ||
    !options ||
    (() => {
      const details = getDetails({ quote: sourceQuote, options: options as OptionsDocument })
      const quoteOptions = getQuoteOptions({ quote: sourceQuote, options: options as OptionsDocument })
      return !details.totals.hours.useCustom || quoteOptions.allowShowCustomizedTotalHours
    })()

  if (sourceQuote && options) {
    const totals = getTotals({ quote: sourceQuote, options: options as OptionsDocument, consumer: 'crew' })
    const { sqftWalls, sqftCeiling, sqftFloor, sqft, lnft } = totals.dimensions
    const totalSqft = sqftWalls + sqftCeiling + sqftFloor + sqft
    if (totalSqft > 0 || lnft > 0) {
      workAreaSummary = {
        walls: sqftWalls > 0 ? sqftWalls : undefined,
        ceilings: sqftCeiling > 0 ? sqftCeiling : undefined,
        floor: sqftFloor > 0 ? sqftFloor : undefined,
        totalSqft: totalSqft > 0 ? totalSqft : undefined,
        totalLnft: lnft > 0 ? lnft : undefined
      }
    }

    const renderableItems: any[] = getItems({
      quote: sourceQuote,
      section: 'bid',
      view: 'area',
      consumer: 'crew',
      options: options as OptionsDocument
    })

    workItems = renderableItems.map((item: any) => {
      const hours =
        Number(item?.additionalFields?.painting ?? 0) + Number(item?.additionalFields?.prep ?? 0) || undefined

      let subareas: PainterWorkSubarea[] | undefined
      const subItems: any[] = item.subItems ?? []
      if (subItems.length > 0) {
        const mapped = subItems.map((sub: any) => {
          const parenIndex = (sub.name as string).indexOf(' (')
          const name = parenIndex === -1 ? sub.name : sub.name.substring(0, parenIndex)
          const subHours = sub.value != null && Number(sub.value) !== 0 ? Number(sub.value) : undefined
          const coats = sub.additionalFields?.coats ? Number(sub.additionalFields.coats) : undefined
          return {
            name,
            hours: subHours,
            description: hasContent(sub.description) ? sub.description : undefined,
            crewNote: hasContent(sub.crewNote) ? sub.crewNote : undefined,
            coats,
            reportText: hasContent(sub.reportText) ? sub.reportText : undefined
          }
        })
        if (mapped.length) subareas = mapped
      }

      const name = typeof item.name === 'string' ? item.name : item.name?.custom || item.name?.default || 'Area'
      const rawDescription = item.description?.useCustom ? item.description?.custom : item.description?.default
      const description = hasContent(rawDescription) ? rawDescription : undefined

      return { id: item.key, name, hours, description, subareas }
    })
  }

  if (!workAreaSummary && !workItems.length) return null

  return (
    <>
      <div className={classes.workDetailsHeading}>Work Details</div>
      {workAreaSummary && <WorkAreaSummaryGrid areas={workAreaSummary} classes={classes} />}
      {workItems.map((item) => (
        <WorkItemRow key={item.id} item={item} showHours={showHours && allowShowHours} classes={classes} />
      ))}
    </>
  )
}
